import React from "react";
import { MessageCircle, Droplets, Sparkles, Sun, Moon } from "lucide-react";
import { IMG, whatsappLink } from "../lib/constants";

export default function SwimmingPool() {
    return (
        <section
            id="pool"
            className="relative bg-[#0d1a14] text-cream py-20 md:py-28"
            data-testid="pool-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                {/* Words */}
                <div className="lg:col-span-5 order-2 lg:order-1">
                    <p className="text-xs tracking-[0.35em] uppercase text-[#E8B769] mb-5" data-testid="pool-overline">
                        Sakleshpur's most loved pool
                    </p>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] mb-7" data-testid="pool-heading">
                        A pool that turns into a<br />
                        <span className="italic text-[#E8B769]">lantern by sunset.</span>
                    </h2>
                    <div className="space-y-5 text-cream/85 text-base md:text-lg leading-relaxed">
                        <p>
                            Set right inside the coffee estate and ringed by hand-painted
                            cart-wheels, our open-air pool is the heart of Veeras — and easily
                            the most beautiful swim in all of Sakleshpur.
                        </p>
                        <p>
                            <span className="text-[#E8B769] font-medium">Perfect for couples</span>{" "}
                            looking for a quiet float at dusk,{" "}
                            <span className="text-[#E8B769] font-medium">families</span> who want a
                            safe, joyful afternoon together, and{" "}
                            <span className="text-[#E8B769] font-medium">friends</span> who
                            simply want a weekend they will keep talking about.
                        </p>
                        <p className="text-cream/70">
                            Crystal-clean water by day · Sunset glow at twilight · Cart-wheel lights by night.
                        </p>
                    </div>

                    <div className="mt-8 grid grid-cols-2 gap-3">
                        {[
                            { icon: Droplets, label: "Crystal clean" },
                            { icon: Sparkles, label: "Cart-wheel deck" },
                            { icon: Sun, label: "Open all day" },
                            { icon: Moon, label: "Lit till late" },
                        ].map((a) => (
                            <div key={a.label} className="flex items-center gap-3 px-4 py-3 bg-white/[0.04] border border-cream/10 rounded-xl">
                                <a.icon className="h-4 w-4 text-[#E8B769] shrink-0" />
                                <span className="text-cream/85 text-sm">{a.label}</span>
                            </div>
                        ))}
                    </div>

                    <a
                        href={whatsappLink("Hi, I'd like to book a poolside stay at Veeras Resorts.")}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-8 inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-4 rounded-full font-medium shadow-lg hover:-translate-y-1 transition-transform"
                        data-testid="pool-whatsapp-btn"
                    >
                        <MessageCircle className="h-5 w-5" />
                        Book the Pool Stay
                    </a>
                </div>

                {/* Photos — 2 large, side stack */}
                <div className="lg:col-span-7 order-1 lg:order-2 grid grid-cols-1 gap-4 md:gap-5 h-[460px] md:h-[620px]">
                    <div className="relative overflow-hidden rounded-2xl group" data-testid="pool-img-day">
                        <img src={IMG.poolDayCollage} alt="Veeras pool by day" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]" />
                        <span className="absolute top-4 left-4 bg-[#E8B769] text-coffee-900 px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded-full">By day</span>
                    </div>
                    <div className="relative overflow-hidden rounded-2xl group" data-testid="pool-img-sunset">
                        <img src={IMG.poolNightCollage} alt="Veeras pool by night" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]" />
                        <span className="absolute top-4 left-4 bg-coffee-900/80 text-cream backdrop-blur-sm px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded-full">By night</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
